import { useCallback, useMemo, useState } from 'react';
import { Button, Confirm, List, Message } from 'semantic-ui-react';
import { itemsBelongToSameAgency } from 'modules/items-belong-to-same-agency/items-belong-to-same-agency';
import WarehousesDropdown from 'components/WarehousesDropdown/WarehousesDropdown';
import CriticalConfirmModal from 'components/Modals/CriticalConfirmModal/CriticalConfirmModal';
import { useAlert } from 'context/AlertProvider';
import { getMessageFromAxiosError } from 'modules/message-factory/message-factory';
import { transferToWarehouse } from 'services/preparation-orders.service';
import { hasMarketplacePreparationOrders } from 'modules/has-marketplace-preparation-orders/has-marketplace-preparation-orders';
import { useWarehouses } from 'hooks/redux/useWarehouses';
import './transfer-preparation-orders-to-warehouse.scss';

export default function TransferPreparationOrdersToWarehouse({
  preparationOrders,
  onTransfer = () => {},
}) {
  const alert = useAlert();
  const { warehouses } = useWarehouses();
  const [currentWarehouse, setCurrentWarehouse] = useState();
  const [
    transferToWarehouseConfirmIsOpen,
    setTransferToWarehouseConfirmIsOpen,
  ] = useState(false);
  const [
    transferToWarehouseReconfirmIsOpen,
    setTransferToWarehouseReconfirmIsOpen,
  ] = useState(false);
  const [loading, setLoading] = useState(false);
  const canTransfer = useMemo(() => {
    return !!(
      !preparationOrders?.length ||
      !itemsBelongToSameAgency(preparationOrders) ||
      hasMarketplacePreparationOrders(preparationOrders)
    );
  }, [preparationOrders]);

  const renderConfirmContent = useCallback(() => {
    return (
      <section className="transfer-to-warehouse-confirm__content">
        <h3>Transferir al almacén</h3>
        <WarehousesDropdown
          warehouses={warehouses}
          value={currentWarehouse}
          onChange={(event, { value }) => setCurrentWarehouse(value)}
        />
        {!!currentWarehouse && (
          <Message info>
            Vas a transferir las siguientes órdenes de preparación al almacén{' '}
            <strong>
              {
                warehouses.find(
                  (warehouse) => warehouse.id === currentWarehouse,
                )?.warehouse
              }
            </strong>
            <List divided verticalAlign="middle">
              {preparationOrders.map((preparationOrder) => (
                <List.Item key={preparationOrder.reference}>
                  <List.Content
                    className="transfer-to-warehouse-confirm__message-row"
                    content={preparationOrder.warehouse.warehouse}
                    description={preparationOrder.reference}
                  />
                </List.Item>
              ))}
            </List>
          </Message>
        )}
      </section>
    );
  }, [currentWarehouse, preparationOrders, warehouses]);

  const handleTransferConfirm = useCallback(async () => {
    setLoading(true);
    setTransferToWarehouseReconfirmIsOpen(false);

    try {
      const references = preparationOrders.map(
        (preparationOrder) => preparationOrder.reference,
      );
      await transferToWarehouse({ references, warehouse_id: currentWarehouse });
      setTransferToWarehouseConfirmIsOpen(false);
      alert.current.message('Operación finalizada con éxito', () => {
        setCurrentWarehouse(null);
        onTransfer();
        setLoading(false);
      });
    } catch (error) {
      alert.current.error(getMessageFromAxiosError(error), () => {
        setLoading(false);
      });
    }
  }, [alert, currentWarehouse, onTransfer, preparationOrders]);

  return (
    <>
      <Button
        primary
        disabled={canTransfer}
        icon="truck"
        content="Transferir a almacén"
        onClick={(event, data) => setTransferToWarehouseConfirmIsOpen(true)}
      />
      <CriticalConfirmModal
        content="¿Confirmas la transferencia de almacén? Esta acción es irreversible."
        confirmableText={`transferir ${
          preparationOrders?.length === 1 ? 'orden' : 'órdenes'
        }`}
        onConfirm={handleTransferConfirm}
        confirmButton="Transferir"
        confirmProps={{
          open: transferToWarehouseReconfirmIsOpen,
          cancelButton: 'Cancelar',
          onCancel: () => setTransferToWarehouseReconfirmIsOpen(false),
        }}
      />
      <Confirm
        className="transfer-to-warehouse-confirm"
        open={transferToWarehouseConfirmIsOpen}
        content={renderConfirmContent()}
        cancelButton={{
          content: 'Cancelar',
          disabled: loading,
          loading,
        }}
        confirmButton={{
          disabled: loading || !currentWarehouse,
          loading,
          primary: true,
          content: 'Transferir',
          onClick: (event, data) => setTransferToWarehouseReconfirmIsOpen(true),
        }}
        onCancel={() => {
          setCurrentWarehouse(null);
          setTransferToWarehouseConfirmIsOpen(false);
        }}
      />
    </>
  );
}
